import React, { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../components/Layout/Layout";
import Loader from "../components/Loader";
import ProjectManagementTable from "../components/ProjectManagement/ProjectManagementTable";
import ProjectManagementForm from "../components/ProjectManagement/ProjectManagementForm";
import ProjectManagementDetails from "../components/ProjectManagement/ProjectManagementDetails";
import api from "../utils/api";
import { PlusIcon, ArrowPathIcon, ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../contexts/UserContext";
import { useParams, useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FiPlus, FiActivity, FiClock } from "react-icons/fi";
import { RiFileExcel2Fill } from "react-icons/ri";

export default function ProjectManagementListPage() {
  const { user } = useUser();
  const { taskId: taskIdFromUrl } = useParams();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusId, setStatusId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [pmModule, setPmModule] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(taskIdFromUrl || null);
  const [summary, setSummary] = useState([]);
  const [custOptions, setCustOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("live"); // "live" or "drafts"
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/project-management/get-drafts");
      if (res.data?.status) {
        setDrafts(res.data.data);
      }
    } catch (err) {
      console.error("Drafts fetch failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = (currentPage - 1) * rowsPerPage;
      const res = await api.get("/project-management", {
        params: {
          limit: rowsPerPage,
          offset: offset,
          search: search,
          statusId: statusId,
          priorityId: priorityId,
          module: pmModule,
          customerId: customerId,
          startDate: startDate,
          endDate: endDate
        },
      });
      if (res.data?.status) {
        setTasks(res.data.data);
        setTotalTasks(res.data.total);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, search, statusId, priorityId, pmModule, customerId, startDate, endDate]);

  const loadResources = useCallback(async () => {
    try {
      const [summRes, custRes, modRes] = await Promise.all([
          api.get("/project-management/status-summary"),
          api.get("/project-management/get-customers"),
          api.get("/project-management/get-system-modules")
      ]);
      if (summRes.data?.status) setSummary(summRes.data.data);
      if (custRes.data?.status) setCustOptions(custRes.data.data);
      if (modRes.data?.status) {
        setModuleOptions(
          modRes.data.data.map((module) => ({
            value: module.value ?? module.id ?? module.module_id ?? module.module_name,
            label: module.label ?? module.module_name ?? module.name ?? "",
          }))
        );
      }
    } catch (err) {
      console.error("Resources fetch failed:", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      if (activeTab === "live") loadTasks();
      else loadDrafts();
      loadResources();
    }
  }, [loadTasks, loadDrafts, loadResources, user, activeTab]);

  useEffect(() => {
    if (taskIdFromUrl) {
        setSelectedTaskId(taskIdFromUrl);
    }
  }, [taskIdFromUrl]);

  const handleTaskClick = (task) => {
    setSelectedTaskId(task.id);
    navigate(`/project-management/${task.id}`);
  };

  const closeDetails = () => {
    setSelectedTaskId(null);
    navigate("/project-management");
  };

  const handleRefresh = () => {
    loadTasks();
    loadResources();
    toast.success("Data refreshed");
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleDelete = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    setTaskToDelete(task);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    
    const taskId = taskToDelete.id;
    setTaskToDelete(null);
    const toastId = toast.loading("Deleting task...");
    try {
        const res = await api.post(`/project-management/delete-task/${taskId}`);
        if (res.data?.status) {
            toast.update(toastId, { render: "Task deleted successfully", type: "success", isLoading: false, autoClose: 3000 });
            loadTasks();
            loadResources();
        }
    } catch (err) {
        console.error("Delete failed:", err);
        toast.update(toastId, { render: "Delete failed", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleExport = async () => {
    const toastId = toast.loading("Generating Excel report...");
    try {
        const res = await api.get("/project-management/export", {
            params: {
                search, statusId, priorityId, 
                module: pmModule, customerId, 
                startDate, endDate
            }
        });
        
        if (res.data?.status) {
            const data = res.data.data;
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Projects Report");

            // 1. Define Columns First (Will start at Row 5)
            sheet.columns = [
                { header: 'TID', key: 'id', width: 10 },
                { header: 'Project Name', key: 'project_name', width: 35 },
                { header: 'Customer', key: 'customer_name', width: 25 },
                { header: 'Project Title', key: 'title', width: 35 },
                { header: 'System Modules', key: 'system_modules', width: 25 },
                { header: 'Module (SOP)', key: 'module_name', width: 20 },
                { header: 'SOP Ref', key: 'sop_ref', width: 15 },
                { header: 'Vertical Head', key: 'vh', width: 25 },
                { header: 'Assigned To', key: 'assigned', width: 35 },
                { header: 'Priority', key: 'priority', width: 12 },
                { header: 'Status', key: 'status', width: 18 },
                { header: 'Start Date', key: 'start', width: 12 },
                { header: 'End Date', key: 'end', width: 12 },
                { header: 'Est. Hours', key: 'est', width: 10 },
                { header: 'Work Hours', key: 'work', width: 10 }
            ];

            // 2. Add Branding Rows (Insert 4 rows at the top)
            sheet.insertRow(1, ["eNova Software and Hardware Solutions Pvt. Ltd"]);
            sheet.insertRow(2, ["REQUIREMENT LIFECYCLE - PROJECT MANAGEMENT REPORT"]);
            sheet.insertRow(3, [`Report Date: ${new Date().toLocaleDateString('en-GB')}`]);
            sheet.insertRow(4, []); // Spacer row

            // Style Row 1 (Company Name)
            sheet.mergeCells('A1:O1');
            const row1 = sheet.getRow(1);
            row1.height = 30;
            const cell1 = row1.getCell(1);
            cell1.font = { name: 'Arial', size: 18, bold: true, color: { argb: 'FFC00000' } }; // Red color
            cell1.alignment = { horizontal: 'center', vertical: 'middle' };

            // Style Row 2 (Report Title)
            sheet.mergeCells('A2:O2');
            const row2 = sheet.getRow(2);
            row2.height = 25;
            const cell2 = row2.getCell(1);
            cell2.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF000000' } };
            cell2.alignment = { horizontal: 'center', vertical: 'middle' };

            // Style Row 3 (Date)
            sheet.mergeCells('A3:O3');
            const row3 = sheet.getRow(3);
            row3.height = 20;
            const cell3 = row3.getCell(1);
            cell3.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF666666' } };
            cell3.alignment = { horizontal: 'center', vertical: 'middle' };

            // 3. Style Header Row (Now Row 5)
            const headerRow = sheet.getRow(5);
            headerRow.height = 30;
            headerRow.eachCell((cell) => {
                cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF000000' } }; // Black text
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }; // Light Gray
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // 4. Add Data
            data.forEach(item => {
                const row = sheet.addRow({
                    id: item.id,
                    project_name: item.project_name,
                    customer_name: item.customer_name,
                    title: item.title,
                    system_modules: item.system_modules || 'N/A',
                    module_name: item.module_name,
                    sop_ref: item.sop_ref,
                    vh: `${item.vh_first_name} ${item.vh_last_name}`,
                    assigned: item.assigned_users || 'Unassigned',
                    priority: item.priority_id == 1 ? 'Low' : item.priority_id == 2 ? 'Medium' : item.priority_id == 3 ? 'High' : 'Critical',
                    status: item.status_name,
                    start: item.expected_start_date,
                    end: item.expected_completion_date,
                    est: Number(item.estimated_hours),
                    work: Number(item.worked_hours)
                });
                row.height = 25;
                row.alignment = { vertical: 'middle' };
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });
            
            // Auto-filter for headers
            sheet.autoFilter = 'A5:O5';

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `Project_Management_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.update(toastId, { render: "Excel exported successfully!", type: "success", isLoading: false, autoClose: 3000 });
        }
    } catch (err) {
        console.error("Export failed:", err);
        toast.update(toastId, { render: "Export failed", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const dashboardCards = useMemo(() => {
      if (!summary) return [];
      
      const grandTotal = summary.reduce((acc, s) => acc + Number(s.count), 0);
      
      // Filter for statuses with actual counts
      const activeStatusCards = summary
        .filter(s => Number(s.count) > 0)
        .map(s => {
            const label = s.status_name;
            const slug = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            return {
                status_id: s.status_id,
                label: label,
                count: Number(s.count),
                bg: `bg-${slug}`
            };
        });

      return [
          { status_id: null, label: 'All Requirements', count: grandTotal, bg: 'bg-all' },
          ...activeStatusCards
      ]
  }, [summary]);

  const getIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes('all')) return <FiActivity className="text-[60px] opacity-10" />;
    if (l.includes('new')) return <FiPlus className="text-[60px] opacity-10" />;
    if (l.includes('design')) return <FiActivity className="text-[60px] opacity-10" />;
    if (l.includes('development')) return <FiClock className="text-[60px] opacity-10" />;
    if (l.includes('implementation')) return <FiActivity className="text-[60px] opacity-10" />;
    if (l.includes('completed')) return <FiActivity className="text-[60px] opacity-10" />;
    return <FiActivity className="text-[60px] opacity-10" />;
  }

  const renderCard = (card) => {
      const isSelected = (statusId === "" && card.status_id === null) || (Number(statusId) === Number(card.status_id) && card.status_id !== null);
      return (
          <div
            key={card.label}
            onClick={() => setStatusId(card.status_id === null ? "" : card.status_id)}
            className={`
              relative flex-none w-44 sm:w-52 h-[105px] p-4 cursor-pointer
              duration-300 shadow-md rounded-2xl transition-all flex flex-col justify-between ${card.bg} text-gray-800
              ${isSelected
                ? "ring-2 ring-red-500 ring-offset-2 shadow-xl scale-[1.03] z-10"
                : "hover:shadow-lg hover:scale-[1.02] hover:-translate-y-1"
              }
            `}
          >
            <div className="text-[10px] font-black relative z-[4] whitespace-normal break-words leading-tight mb-2 uppercase tracking-[0.1em] opacity-60">
              {card.label}
            </div>
            <div className="flex items-end justify-between mt-auto">
              <div className="text-3xl sm:text-4xl font-sans font-black tracking-tighter leading-none">{card.count}</div>
            </div>
            <div className="absolute right-2 bottom-2 opacity-20 pointer-events-none scale-75">
                {getIcon(card.label)}
            </div>
          </div>
      );
  }

  if (!user) return <div className="flex items-center justify-center h-screen"><Loader /></div>;

  return (
    <Layout>
      <ToastContainer />
      <div className="p-4 bg-slate-50 min-h-full space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-gray-800 tracking-tight uppercase">PROJECT WORKFLOW</h1>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1 text-red-600">Premium Lifecycle Tracking</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {String(user?.role_id) !== "2" && (
              <button onClick={handleExport} className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all shadow-sm" title="Export Excel">
                <RiFileExcel2Fill className="w-5 h-5" />
              </button>
            )}
            <button onClick={handleRefresh} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-all shadow-sm">
              <ArrowPathIcon className="w-5 h-5" />
            </button>
            {String(user?.role_id) !== "2" && (
              <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-6 py-2 bg-main text-white rounded-lg text-sm font-bold hover:shadow-lg hover:shadow-main/20 transition-all uppercase tracking-wider">
                <PlusIcon className="w-5 h-5" />
                New Requirement
              </button>
            )}
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="flex flex-wrap gap-4 py-4 px-2">
            {dashboardCards.map(card => renderCard(card))}
        </div>

        {/* Tab Switcher — Only for Admin & Internal Staff */}
        {String(user?.role_id) !== "2" && (
        <div className="flex items-center gap-1 p-1 bg-white border border-gray-100 rounded-2xl w-fit shadow-sm">
            <button 
                onClick={() => setActiveTab("live")}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'live' ? 'bg-main text-white shadow-lg shadow-main/20' : 'text-gray-400 hover:bg-gray-50'}`}
            >
                <FiActivity className={activeTab === 'live' ? 'text-white' : 'text-gray-300'} />
                Requirements ({totalTasks})
            </button>
            <button 
                onClick={() => setActiveTab("drafts")}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'drafts' ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'text-gray-400 hover:bg-gray-50'}`}
            >
                <FiClock className={activeTab === 'drafts' ? 'text-white' : 'text-gray-300'} />
                Drafts ({drafts.length})
            </button>
        </div>
        )}

        {/* Filter Bar */}
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex flex-wrap items-center gap-6">
                {/* Rows Selector */}
                <div className="flex flex-col w-[70px]">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Rows</label>
                    <select 
                        className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20"
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="9999">All</option>
                    </select>
                </div>

                {/* Date Range Selector */}
                <div className="flex flex-col w-full md:w-[400px]">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Created Date Range</label>
                    <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 bg-slate-50 border border-gray-200 rounded-lg p-1.5">
                        <input 
                            type="date" 
                            className="bg-white border border-gray-100 text-[11px] font-bold text-gray-700 px-2 py-1.5 rounded outline-none flex-1 min-w-[110px]"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <span className="text-gray-300 hidden md:inline">→</span>
                        <input 
                            type="date" 
                            className="bg-white border border-gray-100 text-[11px] font-bold text-gray-700 px-2 py-1.5 rounded outline-none flex-1 min-w-[110px]"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                        <button 
                            onClick={() => {
                                const today = new Date().toISOString().split('T')[0];
                                setStartDate(today);
                                setEndDate(today);
                            }}
                            className="px-3 py-1.5 text-[9px] font-black text-red-600 hover:bg-red-50 rounded uppercase border border-red-100 bg-white shadow-sm whitespace-nowrap active:scale-95 transition-all"
                        >Today</button>
                    </div>
                </div>

                {/* Module Filter */}
                <div className="flex flex-col w-full sm:w-[160px]">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Module</label>
                    <select 
                        className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20"
                        value={pmModule}
                        onChange={(e) => setPmModule(e.target.value)}
                    >
                        <option value="">All</option>
                        {moduleOptions.map((m) => (
                          <option key={m.value} value={m.label}>
                            {m.label}
                          </option>
                        ))}
                    </select>
                </div>

                {/* Customer Filter */}
                {String(user?.role_id) !== "2" && (
                    <div className="flex flex-col w-full sm:w-[180px]">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Customer</label>
                        <select 
                            className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20"
                            value={customerId}
                            onChange={(e) => setCustomerId(e.target.value)}
                        >
                            <option value="">All Customers</option>
                            {custOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                )}

                {/* Priority Filter */}
                <div className="flex flex-col w-full sm:w-[160px]">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Priority</label>
                    <select 
                        className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20"
                        value={priorityId}
                        onChange={(e) => setPriorityId(e.target.value)}
                    >
                        <option value="">All Priorities</option>
                        <option value="1">Low</option>
                        <option value="2">Medium</option>
                        <option value="3">High</option>
                        <option value="4">Critical</option>
                    </select>
                </div>

                {/* Search */}
                <div className="flex flex-col w-full md:w-[220px]">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Search Tickets</label>
                    <input 
                        type="text" 
                        placeholder="Search..." 
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20 shadow-inner"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadTasks()}
                    />
                </div>
            </div>
        </div>

        {/* Main Table */}
        <ProjectManagementTable 
          tasks={activeTab === 'live' ? tasks : drafts.map(d => ({ ...d, is_draft_item: true }))}
          onTaskClick={(row) => {
              if (activeTab === 'drafts') {
                  handleEdit(row);
              } else {
                  handleTaskClick(row);
              }
          }}
          isLoading={isLoading}
          totalRows={activeTab === 'live' ? totalTasks : drafts.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onEdit={(row) => handleEdit({ ...row, is_draft_item: activeTab === 'drafts' })}
          onDelete={handleDelete}
        />

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden">
                <div className="bg-slate-50 px-4 sm:px-8 py-4 sm:py-5 border-b flex justify-between items-center shrink-0">
                    <h2 className="text-lg sm:text-xl font-black text-gray-800 tracking-tight">
                        {editingTask ? 'EDIT REQUIREMENT' : 'CREATE NEW REQUIREMENT'}
                    </h2>
                    <button onClick={() => { setShowForm(false); setEditingTask(null); }} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <PlusIcon className="w-6 h-6 rotate-45 text-gray-500" />
                    </button>
                </div>
                <div className="overflow-y-auto custom-scrollbar flex-1 bg-white">
                    <ProjectManagementForm 
                        taskId={editingTask?.is_draft_item ? null : editingTask?.id}
                        initialData={editingTask}
                        onSuccess={(isSavingDraft) => { 
                            if (!isSavingDraft) setActiveTab("live");
                            
                            if (activeTab === "live" || !isSavingDraft) loadTasks();
                            if (activeTab === "drafts" || isSavingDraft) loadDrafts();
                            
                            loadResources(); 
                            const msg = isSavingDraft ? "Draft Saved!" : (editingTask?.is_draft_item ? "Requirement Published to Production!" : (editingTask ? "Requirement updated!" : "Requirement created!"));
                            toast.success(msg); 
                            setShowForm(false); 
                            setEditingTask(null); 
                        }}
                        onClose={() => { setShowForm(false); setEditingTask(null); }}
                    />
                </div>
            </div>
          </div>
        )}

        {/* Details Slider */}
        {selectedTaskId && (
            <ProjectManagementDetails 
                taskId={selectedTaskId}
                onClose={closeDetails}
                onSuccess={() => { loadTasks(); loadResources(); }}
            />
        )}

        {/* Global Delete Confirmation Modal */}
        {taskToDelete && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-red-50">
                    <div className="p-8 text-center space-y-4">
                        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-2 animate-bounce">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        </div>
                        <h3 className="text-xl font-black text-gray-800 tracking-tight">Are you sure?</h3>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed">
                            You are about to delete <span className="text-red-600 font-bold">"{taskToDelete.project_name}"</span>. This action cannot be undone.
                        </p>
                        <div className="flex gap-3 pt-4">
                            <button 
                                onClick={() => setTaskToDelete(null)}
                                className="flex-1 py-3 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDelete}
                                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition-all uppercase tracking-widest shadow-lg shadow-red-200"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
      </div>
    </Layout>
  );
}
